import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarDays, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  minDate?: Date;
  maxDate?: Date;
  /** Extra per-day block on top of minDate/maxDate. Return true to disable the day. */
  disableDay?: (date: Date) => boolean;
  disabled?: boolean;
  modal?: boolean;
}

function toDate(iso: string): Date | undefined {
  if (!iso) return undefined;
  try { return parseISO(iso); } catch { return undefined; }
}

export function DatePicker({
  value, onChange, placeholder = 'Pick a date', className, id,
  minDate, maxDate, disableDay, disabled = false, modal = true,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const date = toDate(value);

  return (
    <Popover modal={modal} open={open} onOpenChange={(v) => { if (!disabled) setOpen(v); }}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-10 w-full justify-start gap-2 text-left font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">
            {date ? format(date, 'dd MMM yyyy') : placeholder}
          </span>
          {value && !disabled && (
            <span
              role="button"
              aria-label="Clear date"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => {
            if (!d) return;
            onChange(format(d, 'yyyy-MM-dd'));
            setOpen(false);
          }}
          disabled={(d) =>
            (minDate !== undefined && d < minDate) ||
            (maxDate !== undefined && d > maxDate) ||
            (disableDay !== undefined && disableDay(d))
          }
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
